import React, { useState, useEffect } from "react";
import { useLocation, useParams, useHistory } from "react-router-dom";
import {
  Stack,
  Grid,
  Container,
  Typography,
  TextField,
  MenuItem,
  FormGroup,
  FormControl,
  Dialog,
  Button,
  Card,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Label from "../../components/Label";
import {
  parseCurrency,
  parseBrandName,
  parseDateTime,
} from "../../utils/tableFunctions";
import {
  getSettlementDetails,
  postApprovedSettlement,
  postRejectedSettlement,
} from "../../services/settlements";
import { Box } from "@mui/system";
import { useUserContext } from "../../context/userContext";
import CircularLoader from "../../components/circularLoader";

const reasonList = [
  {
    value: "Unsettled Listing",
    reason: "Unsettled Listing",
  },
  {
    value: "Testing Fees",
    reason: "Testing Fees",
  },
  {
    value: "Payment Received",
    reason: "Payment Received",
  },
  { value: "Other", reason: "Other" },
];

const SettlementDetails = () => {
  let history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [other, setOther] = useState("");
  const [detail, setDetails] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const handleButtonClick = () => {
    window.close();
  };
  const userRole = useUserContext();

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const status = useQuery().get("status");
  const { id } = useParams();

  useEffect(() => {
    getSettlementDetails(id, setDetails, setLoaded);
  }, [id, loaded, status]);

  const handleDialogOpen = () => {
    setIsOpen(!isOpen);
  };
  const handleDialogClose = () => {
    setIsOpen(false);
    setReason("");
    setOther("");
  };

  const handleDropdownChange = (event) => {
    setReason(event.target.value);
  };

  // Handle for all approve
  const handleApprove = () => {
    if (reason === "" && status !== "pending") {
      alert("A valid reason is required!");
    } else {
      postApprovedSettlement({
        pk_list: [id],
        comment: `${reason === "Other" ? other : reason}`,
      });
      setIsOpen(false);
      setReason("");
      setOther("");
      setTimeout(() => {
        history.replace(`/3/app/details/settlements/${id}/?status=approved`);
      }, 1000);
    }
  };

  const handleRejected = () => {
    if (reason === "") {
      alert("A valid reason is required!");
    } else {
      postRejectedSettlement({
        pk_list: [id],
        comment: `${reason === "Other" ? other : reason}`,
      });
      setIsOpen(false);
      setReason("");
      setOther("");
      setTimeout(
        () =>
          history.replace(`/3/app/details/settlements/${id}/?status=rejected`),
        1000
      );
    }
  };

  const renderDialog = () => {
    return (
      <Dialog open={isOpen} onClose={handleDialogClose} size="">
        <DialogTitle>Reason</DialogTitle>
        <DialogContent>
          <Stack direction="column" minWidth={"400px"}>
            <FormGroup>
              <FormControl required>
                <TextField
                  select
                  fullWidth
                  value={reason}
                  required
                  label="Reason"
                  onChange={handleDropdownChange}
                  sx={{ mt: 2 }}
                >
                  {reasonList.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.reason}
                    </MenuItem>
                  ))}
                </TextField>
                {reason === "Other" && (
                  <TextField
                    value={other}
                    onChange={(e) => setOther(e.target.value)}
                    label="Other reason"
                    sx={{ my: 2 }}
                  />
                )}
              </FormControl>
            </FormGroup>
            <Button
              onClick={status === "rejected" ? handleApprove : handleRejected}
              variant="outlined"
              sx={{ width: "100px", mt: 2, alignSelf: "end" }}
            >
              Submit
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <>
      <Container>
        <Stack
          direction="row"
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography variant="h3" sx={{ mb: 5 }}>
            Settlements Details
          </Typography>
          <Button
            icon="cross"
            large="true"
            text="Back"
            outlined="true"
            onClick={handleButtonClick}
            style={{
              width: "125px",
              height: "15px",
              alignItems: "center",
            }}
          />
        </Stack>
        <Typography>
          {loaded && detail.terminal.is_active
            ? ""
            : "This terminal is inactive"}
        </Typography>
        {loaded ? (
          <Card sx={{ p: 2, mb: 2 }}>
            <Grid
              container
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 2, sm: 2, md: 2 }}
              sx={{ mt: 5 }}
            >
              <Grid item xs={6} md={6}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Settlement ID</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    md={7}
                    sx={{
                      overflowWrap: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {detail.transaction}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Terminal ID</Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    {detail.terminal && detail.terminal.terminal_id}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">
                      Reference Number
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                    md={7}
                    sx={{
                      overflowWrap: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {/* req amount, mdr rate, mdr amount, tr fee, paid amount */}
                    {detail.reference_number}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Merchant</Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    {detail.merchant && detail.merchant.name}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">
                      Transaction Type
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    {detail.transaction_type &&
                      detail.transaction_type.transaction_type}
                  </Grid>
                  {detail.status && (
                    <>
                      <Grid item xs={6} md={5}>
                        <Typography variant="subtitle1">
                          Settlement Completed
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={7}>
                        <Label
                          variant="ghost"
                          color={
                            detail.status === "Approved"
                              ? "success"
                              : detail.status === "Pending"
                              ? "warning"
                              : "error"
                          }
                        >
                          {detail.status}
                        </Label>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>

              <Grid item xs={6} md={6}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">
                      Transaction Time
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    {parseDateTime(detail.transaction_datetime)}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Brand</Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    {detail.payment_brand &&
                      parseBrandName(detail.payment_brand.name)}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Card Number</Typography>
                  </Grid>
                  <Grid item xs={6} md={7}>
                    {detail.card_number}
                  </Grid>

                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">
                      Transaction Amount
                    </Typography>
                  </Grid>
                  <Grid item xs={4} md={7}>
                    {`${detail.transaction_currency} ${parseCurrency(
                      detail.transaction_amount
                    )}`}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">MDR Rate</Typography>
                  </Grid>
                  <Grid item xs={4} md={7}>
                    {detail.mdr_rate && `${detail.mdr_rate} %`}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">MDR Amount</Typography>
                  </Grid>
                  <Grid item xs={4} md={7}>
                    {`${detail.transaction_currency} ${parseCurrency(
                      detail.mdr_amount
                    )}`}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Transaction Fee</Typography>
                  </Grid>
                  <Grid item xs={4} md={7}>
                    {detail.transaction_fee &&
                      `${detail.transaction_currency} ${parseCurrency(
                        detail.transaction_fee
                      )}`}
                  </Grid>
                  <Grid item xs={6} md={5}>
                    <Typography variant="subtitle1">Net Amount</Typography>
                  </Grid>
                  <Grid item xs={4} md={7}>
                    {`${detail.paid_currency} ${parseCurrency(
                      detail.paid_amount
                    )}`}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {status &&
              status !== "transaction" &&
              userRole?.reconsettlement?.includes("Approve/Reject") && (
                <Box
                  style={{
                    marginTop: "40px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {(status.toLowerCase() === "pending" ||
                    status.toLowerCase() === "approved") && (
                    <Button
                      color="error"
                      onClick={handleDialogOpen}
                      variant="outlined"
                    >
                      Reject
                    </Button>
                  )}
                  <Box sx={{ width: 15 }} />

                  {status && status.toLowerCase() === "pending" && (
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={handleApprove}
                    >
                      Approve
                    </Button>
                  )}
                  {status && status.toLowerCase() === "rejected" && (
                    <Button onClick={handleDialogOpen} variant="outlined">
                      Approve
                    </Button>
                  )}
                </Box>
              )}
            {renderDialog()}
          </Card>
        ) : (
          <CircularLoader />
        )}
      </Container>
    </>
  );
};

export default SettlementDetails;
